
import { CleanVehicleData } from './cleanVehicleObject';

export function parseVehicleData(raw: any, styleIndex = 0): CleanVehicleData {
  const response = raw?.query_responses?.['Request-Sample'];
  const marketData = response?.us_market_data?.common_us_data;
  const styles = response?.us_market_data?.us_styles || [];
  const style = styles[styleIndex];
  if (styles.length === 0) {
    console.warn("⚠️ us_styles is EMPTY or missing.");
    throw new Error("❌ No styles found in the response.");
  }
  
  console.log("✅ Using style at index", styleIndex, ":", style);
  
  if (!style.engines || style.engines.length === 0) {
    console.warn("⚠️ No engine data in selected style.");
  }
  
  if (!style.transmissions || style.transmissions.length === 0) {
    console.warn("⚠️ No transmission data in selected style.");
  }
  
  console.log("🟡 us_styles array length:", styles.length);
  console.dir(styles, { depth: null });
  console.log("✅ Using style at index", styleIndex, ":", style);
  
  if (!marketData || !style) {
    throw new Error("❌ Invalid vehicle data: common_us_data or selected style missing.");
  }

  const extractSpecs = (category: string): Record<string, string> => {
    const block = style.standard_specifications?.find((s) => s.specification_category === category);
    const result: Record<string, string> = {};
    block?.specification_values?.forEach((v) => {
      if (typeof v.specification_value === 'string') {
        result[v.specification_name] = v.specification_value;
      } else if (Array.isArray(v.specification_value)) {
        result[v.specification_name] = v.specification_value.map(item => item.generic_equipment_name).join(', ');
      }
    });
    return result;
  };

  const extractEquipment = (): Record<string, Record<string, string[]>> => {
    const groupedEquipment: Record<string, Record<string, string[]>> = {};
    const groups = marketData?.standard_generic_equipment || [];

    for (const group of groups) {
      const groupName = group.generic_equipment_category_group || 'Misc';
      groupedEquipment[groupName] ||= {};
      for (const category of group.generic_equipment_categories || []) {
        const categoryName = category.generic_equipment_category;
        groupedEquipment[groupName][categoryName] ||= [];
        for (const equip of category.generic_equipment || []) {
          const equipName = equip.generic_equipment_name;
          const values = (equip.generic_equipment_values || [])
            .map(val => val?.generic_equipment_value)
            .filter((val): val is string => typeof val === 'string');

          groupedEquipment[groupName][categoryName].push(
            values.length > 0 ? `${equipName}: ${values.join(', ')}` : equipName
          );
        }
      }
    }

    return groupedEquipment;
  };

  const extractOemOptions = (): CleanVehicleData["oemOptions"] => {
    const options: CleanVehicleData["oemOptions"] = [];
    for (const group of style.oem_options || []) {
      for (const cat of group.oem_option_categories || []) {
        for (const opt of cat.oem_option_values || []) {
          options.push({
            group: group.oem_option_category_group,
            category: cat.oem_option_category,
            name: opt.oem_option_name,
            code: opt.oem_option_code,
            description: opt.oem_option_description || '',
            invoice: opt.oem_option_invoice_price || '',
            msrp: opt.oem_option_msrp || '',
            installed: opt.installed || '',
          });
        }
      }
    }
    return options;
  };

  const extractColors = (): CleanVehicleData["colors"] => {
    const ext = marketData?.colors?.exterior_colors || [];
    const int = marketData?.colors?.interior_colors || [];
    return {
      exterior: ext.map((c) => ({
        id: c.ext_color_id,
        mfrCode: c.mfr_code,
        genericColor: c.generic_color_name,
        mfrColor: c.mfr_color_name,
        hex: c.primary_rgb_code?.hex || '',
        r: c.primary_rgb_code?.r || '',
        g: c.primary_rgb_code?.g || '',
        b: c.primary_rgb_code?.b || '',
        twoTone: c.two_tone,
        fleet: c.fleet,
        secondaryHex: c.secondary_rgb_code?.hex || '',
        secondaryR: c.secondary_rgb_code?.r || '',
        secondaryG: c.secondary_rgb_code?.g || '',
        secondaryB: c.secondary_rgb_code?.b || ''
      })),
      interior: int.map((c) => ({
        id: c.int_color_id,
        mfrCode: c.mfr_code,
        genericColor: c.generic_color_name,
        mfrColor: c.mfr_color_name,
        hex: c.primary_rgb_code?.hex || '',
        r: c.primary_rgb_code?.r || '',
        g: c.primary_rgb_code?.g || '',
        b: c.primary_rgb_code?.b || '',
        twoTone: c.two_tone,
        fleet: c.fleet,
        secondaryHex: c.secondary_rgb_code?.hex || '',
        secondaryR: c.secondary_rgb_code?.r || '',
        secondaryG: c.secondary_rgb_code?.g || '',
        secondaryB: c.secondary_rgb_code?.b || ''
      }))
    };
  };

  const validateUrl = (url: string): string => {
    if (!url) return '';
    const full = url.startsWith('http') ? url : `https://${url}`;
    try {
      new URL(full);
      return full;
    } catch {
      return '';
    }
  };

  const allEvoxImages = marketData?.media?.evox_colors_single_640px || [];
  const asBuiltColorId = marketData?.colors?.exterior_colors?.[0]?.ext_color_id || '';

  return {
    decoderMessages: {
      serviceProvider: raw?.decoder_messages?.service_provider || '',
      version: raw?.decoder_messages?.version || '',
      errors: raw?.decoder_messages?.errors || [],
    },
    transactionId: response?.transaction_id || '',

    basicData: {
      year: style.basic_data?.year || '',
      make: style.basic_data?.make || '',
      model: style.basic_data?.model || '',
      trim: style.basic_data?.trim || '',
      driveType: style.basic_data?.drive_type || '',
      vehicleType: style.basic_data?.vehicle_type || '',
      bodyType: style.basic_data?.body_type || '',
      doors: style.basic_data?.doors || '',
      brakeSystem: style.basic_data?.brake_system || '',
      restraintType: style.basic_data?.restraint_type || '',
      plant: style.basic_data?.plant || '',
      countryOfManufacture: style.basic_data?.country_of_manufacture || '',
    },

    pricing: {
      msrp: style.pricing?.msrp || '',
      invoice: style.pricing?.invoice_price || '',
      destination: style.pricing?.destination_charge || '',
      gasGuzzlerTax: style.pricing?.gas_guzzler_tax || ''
    },

    engine: {
      name: style.engines?.[0]?.name || '',
      type: style.engines?.[0]?.engine_type || '',
      cylinders: style.engines?.[0]?.ice_cylinders || '',
      displacement: style.engines?.[0]?.ice_displacement || '',
      hp: style.engines?.[0]?.ice_max_hp || '',
      torque: style.engines?.[0]?.ice_max_torque || '',
      fuelType: style.engines?.[0]?.fuel_type || '',
      oilCapacity: style.engines?.[0]?.oil_capacity || ''
    },

    transmission: {
      name: style.transmissions?.[0]?.name || '',
      type: style.transmissions?.[0]?.type || '',
      gears: style.transmissions?.[0]?.gears || '',
      marketingName: style.transmissions?.[0]?.marketing_name || ''
    },

    specs: {
      brakes: extractSpecs('Brakes'),
      drivetrain: extractSpecs('Drivetrain'),
      exteriorDimensions: extractSpecs('Exterior Dimensions'),
      interiorDimensions: extractSpecs('Interior Dimensions'),
      seating: extractSpecs('Seating'),
      weightsAndCapacities: extractSpecs('Weights and Capacities'),
      wheelsAndTires: extractSpecs('Wheels and Tires')
    },

    equipment: extractEquipment(),
    oemOptions: extractOemOptions(),
    colors: extractColors(),
    safetyEquipment: marketData?.safety_equipment || {},

    warranties: (marketData?.warranties || []).map(w => ({
      name: w.name || '',
      type: w.type || '',
      months: w.months || '',
      miles: w.miles || ''
    })),

    epaFuelEfficiency: (marketData?.epa_fuel_efficiency || []).map(e => ({
      engineId: e.engine_id || '',
      transmissionId: e.transmission_id || '',
      fuelType: e.fuel_type || '',
      city: e.city || '',
      highway: e.highway || '',
      combined: e.combined || ''
    })),

    advanced_driver_assistance_systems: style.advanced_driver_assistance_systems || [],

    evoxImages: allEvoxImages.map((img) => ({
      angleId: img.angle_id,
      angleDescription: img.angle_description,
      extColorId: img.ext_color_id,
      fullsize: validateUrl(img.fullsize_path),
      midsize: validateUrl(img.midsize_path),
      thumbnail: validateUrl(img.thumbnail_path),
      isMatch: img.ext_color_id === asBuiltColorId,
      matchedColorName:
        img.ext_color_id === asBuiltColorId
          ? marketData?.colors?.exterior_colors.find(c => c.ext_color_id === img.ext_color_id)?.mfr_color_name || ''
          : '',
    })),

    matchAccuracy: {
      total: 8,
      matched: Object.entries(marketData?.media?.evox_mapping || {}).filter(([_, v]) => v === 'Y').length,
      mapping: marketData?.media?.evox_mapping || {}
    }
  };
}
